import React from 'react';
import { cn } from '../../lib/utils';
import { getPriority, PRIORITY_META } from '../../design-system/priority';

/**
 * OrkaPriorityChip — canonical priority display (PRD §6.5, Rule 2).
 *
 * Replaces inline priority colour maps in TaskCard, BacklogPane
 * (`PRIORITY_BORDER_COLORS`), SmartField, and any other surface.
 *
 *   <OrkaPriorityChip priority={-1} />              // numeric → emergency
 *   <OrkaPriorityChip priority="high" variant="rail" />
 */
export function OrkaPriorityChip({
  priority,
  variant = 'chip',  // 'chip' | 'rail' | 'icon'
  size = 'md',
  className,
  ...rest
}) {
  const meta = PRIORITY_META[priority] || getPriority(priority);

  if (variant === 'rail') {
    return (
      <span
        aria-label={meta.label}
        className={cn('rounded-full shrink-0', className)}
        style={{ width: 3, height: '100%', backgroundColor: meta.color, minHeight: 16 }}
        {...rest}
      />
    );
  }

  if (variant === 'icon') {
    return (
      <span
        aria-label={meta.label}
        className={cn('inline-block rounded-full shrink-0', className)}
        style={{ width: 8, height: 8, backgroundColor: meta.color }}
        {...rest}
      />
    );
  }

  // chip variant
  const SIZE = {
    sm: 'text-[10px] h-5 px-1.5',
    md: 'text-xs h-6 px-2',
  };
  return (
    <span
      className={cn(
        'inline-flex items-center font-mono font-semibold rounded-md select-none',
        SIZE[size] || SIZE.md,
        className,
      )}
      style={{
        backgroundColor: meta.soft,
        color: meta.text,
      }}
      title={meta.label}
      data-priority={meta.key}
      {...rest}
    >
      {meta.short}
    </span>
  );
}

export default OrkaPriorityChip;
